import React, { useCallback, useMemo, useState } from "react";
import { Slider } from "antd";
import { cn } from "@/lib/utils";
import { colors } from "../../theme/colors";
import { Label } from "../label";

export type RangeSliderSize = "small" | "medium" | "large";

export interface RangeSliderProps {
  label?: string;
  value: number;
  onChange: (value: number) => void;
  onAfterChange?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  required?: boolean;
  disabled?: boolean;
  size?: RangeSliderSize;
  className?: string;
  showMinMax?: boolean;
  showValue?: boolean;
  helperText?: string;
  error?: string;
  marks?: Record<number, string>;
  showTooltip?: boolean;
  trackColor?: string;
}

const sizeConfig: Record<
  RangeSliderSize,
  {
    trackHeight: number;
    handleSize: number;
    minValueBoxWidth: number;
    valueBoxHeight: number;
    valueBoxPadding: number;
    fontSize: string;
    labelSize: "small" | "medium" | "large";
  }
> = {
  small: {
    trackHeight: 4,
    handleSize: 14,
    minValueBoxWidth: 48,
    valueBoxHeight: 28,
    valueBoxPadding: 8,
    fontSize: "text-xs",
    labelSize: "small",
  },
  medium: {
    trackHeight: 6,
    handleSize: 18,
    minValueBoxWidth: 56,
    valueBoxHeight: 32,
    valueBoxPadding: 10,
    fontSize: "text-sm",
    labelSize: "medium",
  },
  large: {
    trackHeight: 8,
    handleSize: 22,
    minValueBoxWidth: 64,
    valueBoxHeight: 36,
    valueBoxPadding: 12,
    fontSize: "text-base",
    labelSize: "large",
  },
};

export const RangeSlider: React.FC<RangeSliderProps> = ({
  label,
  value,
  onChange,
  onAfterChange,
  min = 0,
  max = 100,
  step = 1,
  unit = "px",
  required = false,
  disabled = false,
  size = "medium",
  className,
  showMinMax = true,
  showValue = true,
  helperText,
  error,
  marks,
  showTooltip = false,
  trackColor,
}) => {
  const config = sizeConfig[size];
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(String(value));

  const percentage = useMemo(() => {
    return ((value - min) / (max - min)) * 100;
  }, [value, min, max]);

  const handleSliderChange = useCallback(
    (newValue: number) => {
      onChange(newValue);
      setInputValue(String(newValue));
    },
    [onChange]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    []
  );

  const handleInputBlur = useCallback(() => {
    setIsEditing(false);
    const parsed = parseInt(inputValue, 10);
    if (!isNaN(parsed)) {
      const clamped = Math.max(min, Math.min(max, parsed));
      onChange(clamped);
      setInputValue(String(clamped));
    } else {
      setInputValue(String(value));
    }
  }, [inputValue, min, max, onChange, value]);

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleInputBlur();
      } else if (e.key === "Escape") {
        setIsEditing(false);
        setInputValue(String(value));
      }
    },
    [handleInputBlur, value]
  );

  const handleInputFocus = useCallback(() => {
    setIsEditing(true);
  }, []);

  const activeColor = trackColor || colors.primary;

  const sliderStyles = useMemo(
    () => ({
      track: {
        backgroundColor: disabled ? colors.textMuted : activeColor,
        height: config.trackHeight,
      },
      rail: {
        backgroundColor: colors.surfaceSubtle,
        height: config.trackHeight,
      },
      handle: {
        backgroundColor: disabled ? colors.textMuted : activeColor,
        borderColor: colors.white,
        borderWidth: 3,
        width: config.handleSize,
        height: config.handleSize,
        marginTop: -(config.handleSize - config.trackHeight) / 2,
        boxShadow: `0 2px 6px ${colors.black}33`,
      },
    }),
    [disabled, config, activeColor]
  );

  const valueBoxMarginTop = 8;
  const minMaxMarginTop = showValue
    ? config.valueBoxHeight + valueBoxMarginTop + 12
    : 8;

  return (
    <div className={cn("flex flex-col select-none", className)}>
      {label && (
        <Label
          size={config.labelSize}
          weight={500}
          required={required}
          disabled={disabled}
        >
          {label}
        </Label>
      )}

      <div className="relative w-full pt-2">
        <Slider
          value={value}
          onChange={handleSliderChange}
          onChangeComplete={onAfterChange}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          marks={marks}
          tooltip={{ open: showTooltip ? undefined : false }}
          styles={{
            track: sliderStyles.track,
            rail: sliderStyles.rail,
            handle: sliderStyles.handle,
          }}
          className="notifyy-range-slider"
        />

        {showValue && (
          <div
            className="absolute top-full pointer-events-auto"
            style={{
              left: `${percentage}%`,
              transform: "translateX(-50%)",
              marginTop: valueBoxMarginTop,
              transition: isEditing ? "none" : "left 0.1s ease-out",
            }}
          >
            <div
              className={cn(
                "flex items-center justify-center rounded border whitespace-nowrap",
                config.fontSize,
                "font-medium transition-colors duration-150",
                isEditing && "ring-2 ring-offset-1"
              )}
              style={{
                minWidth: config.minValueBoxWidth,
                height: config.valueBoxHeight,
                paddingLeft: config.valueBoxPadding,
                paddingRight: config.valueBoxPadding,
                borderColor: error ? colors.error : colors.surfaceSubtle,
                backgroundColor: colors.white,
                ...(isEditing && { ringColor: colors.primary }),
              }}
            >
              <input
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onFocus={handleInputFocus}
                onKeyDown={handleInputKeyDown}
                disabled={disabled}
                className={cn(
                  "h-full text-center bg-transparent border-none outline-none appearance-none",
                  disabled && "cursor-not-allowed opacity-50"
                )}
                style={{
                  color: colors.textDefault,
                  width: `${String(inputValue).length + 1}ch`,
                  minWidth: "2ch",
                }}
              />
              <span
                className="text-xs shrink-0"
                style={{ color: colors.textMuted }}
              >
                {unit}
              </span>
            </div>
          </div>
        )}
      </div>

      {showMinMax && (
        <div
          className="flex items-center justify-between w-full"
          style={{ marginTop: minMaxMarginTop }}
        >
          <span
            className="text-xs select-none"
            style={{ color: colors.textMuted }}
          >
            {min}
            {unit}
          </span>
          <span
            className="text-xs select-none"
            style={{ color: colors.textMuted }}
          >
            {max}
            {unit}
          </span>
        </div>
      )}

      {(helperText || error) && (
        <span
          className="text-xs mt-1"
          style={{ color: error ? colors.error : colors.textMuted }}
        >
          {error || helperText}
        </span>
      )}

      <style>{`
        .notifyy-range-slider.ant-slider {
          margin: 0;
          padding: 0;
        }
        .notifyy-range-slider .ant-slider-handle {
          cursor: pointer;
          border-radius: 50% !important;
          outline: none !important;
        }
        .notifyy-range-slider .ant-slider-handle:hover,
        .notifyy-range-slider .ant-slider-handle:focus,
        .notifyy-range-slider .ant-slider-handle:focus-visible {
          border-color: ${colors.white} !important;
          box-shadow: 0 2px 8px ${colors.black}40 !important;
          outline: none !important;
        }
        .notifyy-range-slider .ant-slider-handle::after,
        .notifyy-range-slider .ant-slider-handle::before {
          display: none !important;
        }
        .notifyy-range-slider.ant-slider-disabled .ant-slider-handle {
          cursor: not-allowed;
        }
        .notifyy-range-slider .ant-slider-track,
        .notifyy-range-slider .ant-slider-rail {
          border-radius: 999px;
        }
      `}</style>
    </div>
  );
};

export default RangeSlider;
